import jsPDF from 'jspdf';
import dayjs from './dayjs';
import { config } from '../config/env';
import { useAppStore } from '../stores/useAppStore';

/**
 * Purchase order PDF, downloaded the moment a purchase goes live.
 *
 * A draft never produces one — it has not touched stock yet, so there is
 * nothing to file against a delivery. The document is generated the first time
 * the purchase becomes live, whether that is a direct "Create Purchase" or a
 * later "Go Live" on a draft.
 *
 * Built with jsPDF rather than the accoutre-react-pdf receipt components
 * because this is an A4 checklist for the stockroom, not a thermal receipt, and
 * a CSV import can push it to hundreds of rows across many pages.
 */

// A4 portrait in millimetres.
const PAGE = { width: 210, height: 297 };
const MARGIN = 14;
const CONTENT_WIDTH = PAGE.width - MARGIN * 2; // 182mm

// Widths add up to CONTENT_WIDTH. The item column takes what is left over
// after the numbers, which need a fixed width to stay readable when right
// aligned. Barcodes in this catalogue are short numerics (7 digits at most).
const COLUMNS = [
  { key: 'index', label: '#', width: 8, align: 'left' },
  { key: 'name', label: 'Item', width: 74, align: 'left' },
  { key: 'barcode', label: 'Barcode', width: 16, align: 'left' },
  { key: 'quantity', label: 'Qty', width: 16, align: 'right' },
  { key: 'price', label: 'Unit price', width: 24, align: 'right' },
  { key: 'discount', label: 'Disc/unit', width: 22, align: 'right' },
  { key: 'total', label: 'Line total', width: 22, align: 'right' },
];

const ROW_FONT_SIZE = 8;
const ROW_LINE_HEIGHT = 3.6;
const ROW_PADDING = 1.6;
// A long name wraps, but only so far — beyond this the tail is ellipsised so
// one badly named item cannot push the table to a page per row.
const MAX_NAME_LINES = 2;

const TABLE_HEADER_HEIGHT = 7;
const FOOTER_HEIGHT = 12;

const num = (value) => {
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Thousands separators, two decimals. The rupee sign is not in jsPDF's built-in
// WinAnsi fonts, so amounts carry an "Rs" word where a symbol is wanted and the
// table cells stay bare numbers under an "Rs" column note.
const money = (value) =>
  num(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Quantities are whole units in practice; only show decimals when there are any.
const qty = (value) => {
  const n = num(value);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

/**
 * Totals for the whole purchase.
 *
 * Deliberately the same arithmetic as TotalsBar and the purchase detail table:
 * a line is (Price - Discount) x Quantity, with Discount being per unit, and
 * the purchase-level Discount comes off the subtotal. If those ever diverge the
 * PDF would quietly disagree with the screen it was printed from.
 */
const summarise = (details, overallDiscount) => {
  const rows = details || [];
  let quantity = 0;
  let subtotal = 0;

  rows.forEach((detail) => {
    const q = num(detail?.Quantity);
    quantity += q;
    subtotal += (num(detail?.Price) - num(detail?.Discount)) * q;
  });

  const discount = num(overallDiscount);
  return { lineItems: rows.length, quantity, subtotal, discount, total: subtotal - discount };
};

/** Wrapped item name, capped at MAX_NAME_LINES with an ellipsis on the last. */
const nameLines = (doc, name, width) => {
  const text = String(name || 'Unnamed item');
  const lines = doc.splitTextToSize(text, width);
  if (lines.length <= MAX_NAME_LINES) return lines;

  const kept = lines.slice(0, MAX_NAME_LINES);
  const last = kept[MAX_NAME_LINES - 1];
  // Trim until the ellipsis fits rather than assuming a character count.
  let truncated = last;
  while (truncated.length > 1 && doc.getTextWidth(`${truncated}...`) > width) {
    truncated = truncated.slice(0, -1);
  }
  kept[MAX_NAME_LINES - 1] = `${truncated.trimEnd()}...`;
  return kept;
};

/** Draws one cell, right-aligning against the far edge of its column. */
const drawCell = (doc, text, x, y, column) => {
  if (column.align === 'right') {
    doc.text(text, x + column.width - 1.5, y, { align: 'right' });
  } else {
    doc.text(text, x + 1.5, y);
  }
};

const drawTableHeader = (doc, y) => {
  doc.setFillColor(241, 245, 249);
  doc.rect(MARGIN, y, CONTENT_WIDTH, TABLE_HEADER_HEIGHT, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(51, 65, 85);

  let x = MARGIN;
  COLUMNS.forEach((column) => {
    drawCell(doc, column.label, x, y + 4.8, column);
    x += column.width;
  });

  doc.setTextColor(17, 24, 39);
  return y + TABLE_HEADER_HEIGHT;
};

/** Business name, contact details and the purchase's identity block. */
const drawDocumentHeader = (doc, purchase, businessInfo) => {
  let y = MARGIN;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(businessInfo.BusinessName || config.appTitle, MARGIN, y + 5);

  // Right-hand identity block, aligned to the page edge.
  doc.setFontSize(13);
  doc.text(`PURCHASE #${purchase.ID}`, PAGE.width - MARGIN, y + 5, { align: 'right' });

  y += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(90, 98, 112);

  const contact = [businessInfo.BusinessAddress, businessInfo.BusinessPhone].filter(Boolean);
  contact.forEach((line) => {
    doc.text(String(line), MARGIN, y);
    y += 4;
  });

  doc.setTextColor(17, 24, 39);
  y += contact.length ? 2 : 4;

  doc.setDrawColor(203, 213, 225);
  doc.line(MARGIN, y, PAGE.width - MARGIN, y);
  y += 6;

  // Two columns of metadata so the block stays short.
  const purchaseDate = purchase.PurchaseDate
    ? dayjs(purchase.PurchaseDate).format('DD MMM YYYY')
    : '-';
  const createdDate = purchase.CreatedDate
    ? dayjs(purchase.CreatedDate).format('DD MMM YYYY, h:mm A')
    : '-';

  const meta = [
    ['Purchase date', purchaseDate, 'Status', 'Live'],
    ['Created', createdDate, 'Generated', dayjs().format('DD MMM YYYY, h:mm A')],
  ];

  doc.setFontSize(8.5);
  meta.forEach(([leftLabel, leftValue, rightLabel, rightValue]) => {
    doc.setTextColor(107, 114, 128);
    doc.text(`${leftLabel}:`, MARGIN, y);
    doc.text(`${rightLabel}:`, MARGIN + 95, y);

    doc.setTextColor(17, 24, 39);
    doc.setFont('helvetica', 'bold');
    doc.text(String(leftValue), MARGIN + 24, y);
    doc.text(String(rightValue), MARGIN + 116, y);
    doc.setFont('helvetica', 'normal');
    y += 4.6;
  });

  doc.setTextColor(17, 24, 39);
  return y + 3;
};

/** "Page n of m" on every page, stamped once the total is known. */
const drawFooters = (doc, purchase) => {
  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(140, 148, 160);
    doc.text(`Purchase #${purchase.ID}`, MARGIN, PAGE.height - 8);
    doc.text(`Page ${page} of ${pageCount}`, PAGE.width - MARGIN, PAGE.height - 8, {
      align: 'right',
    });
  }
};

/**
 * Renders the purchase to a jsPDF document.
 * Exported for tests; callers want downloadPurchasePdf below.
 */
export const buildPurchasePdf = (purchase, businessInfo = {}) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const details = purchase.details || [];
  const totals = summarise(details, purchase.Discount);

  let y = drawDocumentHeader(doc, purchase, businessInfo);
  y = drawTableHeader(doc, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(ROW_FONT_SIZE);

  const nameColumn = COLUMNS.find((column) => column.key === 'name');
  const bodyBottom = PAGE.height - MARGIN - FOOTER_HEIGHT;

  details.forEach((detail, index) => {
    const lines = nameLines(doc, detail?.item?.Name, nameColumn.width - 3);
    const rowHeight = lines.length * ROW_LINE_HEIGHT + ROW_PADDING * 2;

    // Break before drawing, so a row is never split across two pages.
    if (y + rowHeight > bodyBottom) {
      doc.addPage();
      y = MARGIN;
      y = drawTableHeader(doc, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(ROW_FONT_SIZE);
    }

    // Zebra striping keeps a long delivery checklist trackable by eye.
    if (index % 2 === 1) {
      doc.setFillColor(249, 250, 251);
      doc.rect(MARGIN, y, CONTENT_WIDTH, rowHeight, 'F');
    }

    const baseline = y + ROW_PADDING + 2.6;
    const lineTotal = (num(detail?.Price) - num(detail?.Discount)) * num(detail?.Quantity);
    const values = {
      index: String(index + 1),
      barcode: detail?.item?.Barcode ? String(detail.item.Barcode) : '-',
      quantity: qty(detail?.Quantity),
      price: money(detail?.Price),
      discount: money(detail?.Discount),
      total: money(lineTotal),
    };

    let x = MARGIN;
    COLUMNS.forEach((column) => {
      if (column.key === 'name') {
        lines.forEach((line, lineIndex) => {
          doc.text(line, x + 1.5, baseline + lineIndex * ROW_LINE_HEIGHT);
        });
      } else {
        drawCell(doc, values[column.key], x, baseline, column);
      }
      x += column.width;
    });

    y += rowHeight;
  });

  if (details.length === 0) {
    doc.setTextColor(107, 114, 128);
    doc.text('No items on this purchase.', MARGIN + 1.5, y + 5);
    doc.setTextColor(17, 24, 39);
    y += 9;
  }

  doc.setDrawColor(203, 213, 225);
  doc.line(MARGIN, y, PAGE.width - MARGIN, y);
  y += 6;

  // Totals block, right aligned under the money columns. Keep it whole: move it
  // to a new page rather than let the total land alone under a page break.
  const totalRows = [
    ['Line items', String(totals.lineItems)],
    ['Total quantity', qty(totals.quantity)],
    ['Subtotal', `Rs ${money(totals.subtotal)}`],
  ];
  if (totals.discount !== 0) {
    totalRows.push(['Purchase discount', `- Rs ${money(totals.discount)}`]);
  }

  const totalsHeight = totalRows.length * 5 + 9;
  if (y + totalsHeight > PAGE.height - MARGIN - FOOTER_HEIGHT) {
    doc.addPage();
    y = MARGIN;
  }

  const labelX = PAGE.width - MARGIN - 42;
  const valueX = PAGE.width - MARGIN;

  doc.setFontSize(9);
  totalRows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    doc.text(label, labelX, y, { align: 'right' });
    doc.setTextColor(17, 24, 39);
    doc.text(value, valueX, y, { align: 'right' });
    y += 5;
  });

  y += 1;
  doc.setDrawColor(148, 163, 184);
  doc.line(labelX - 24, y, valueX, y);
  y += 5.5;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Total', labelX, y, { align: 'right' });
  doc.text(`Rs ${money(totals.total)}`, valueX, y, { align: 'right' });
  y += 10;

  // Remarks last — they are context, not something to scan a delivery against.
  const remarks = [
    ['Purchase remarks', purchase.PurchaseRemarks],
    ['Additional remarks', purchase.Remarks],
  ].filter(([, value]) => value);

  if (remarks.length > 0) {
    if (y + remarks.length * 10 > PAGE.height - MARGIN - FOOTER_HEIGHT) {
      doc.addPage();
      y = MARGIN;
    }

    remarks.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(107, 114, 128);
      doc.text(label, MARGIN, y);
      y += 4.4;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(17, 24, 39);
      doc.splitTextToSize(String(value), CONTENT_WIDTH).forEach((line) => {
        if (y > PAGE.height - MARGIN - FOOTER_HEIGHT) {
          doc.addPage();
          y = MARGIN;
        }
        doc.text(line, MARGIN, y);
        y += 4;
      });
      y += 3;
    });
  }

  drawFooters(doc, purchase);
  return doc;
};

export const purchasePdfFileName = (purchase) => {
  const date = purchase.PurchaseDate
    ? dayjs(purchase.PurchaseDate).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');
  return `purchase-${purchase.ID}-${date}.pdf`;
};

/**
 * Generates the purchase PDF and hands it to the browser as a download.
 *
 * Throws on failure. Callers have already created or published the purchase by
 * this point, so a failure here must not be reported as the purchase failing —
 * see the call sites, which downgrade it to a warning.
 */
export const downloadPurchasePdf = (purchase) => {
  if (!purchase || purchase.ID == null) {
    throw new Error('downloadPurchasePdf: missing purchase');
  }

  const { businessInfo } = useAppStore.getState();
  const doc = buildPurchasePdf(purchase, businessInfo || {});
  doc.save(purchasePdfFileName(purchase));
};
